import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Upload, Trash2, FolderOpen, Download, BookOpen, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [category, setCategory] = useState('training');
  const [userRole, setUserRole] = useState('staff');
  const [managementOnly, setManagementOnly] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Get user role
    const user = localStorage.getItem('vyrus_user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        setUserRole(userData.role || 'staff');
      } catch (e) {}
    }
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/documents/company`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!title || !fileUrl) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.post(
        `${BACKEND_URL}/api/documents/company?title=${encodeURIComponent(title)}&file_url=${encodeURIComponent(fileUrl)}&category=${category}&management_only=${managementOnly}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Document uploaded');
      setUploadDialogOpen(false);
      setTitle('');
      setFileUrl('');
      setManagementOnly(false);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to upload document');
      console.error(error);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/documents/company/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
      console.error(error);
    }
  };

  const groupByCategory = () => {
    const grouped = {};
    documents.forEach((doc) => {
      const cat = doc.category || 'general';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(doc);
    });
    return grouped;
  };

  const grouped = groupByCategory();
  const isManagement = userRole === 'management';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight" data-testid="documents-title">
            {isManagement ? 'Vyrus Trainings & Docs' : 'Documents'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isManagement ? 'Company documents and training materials' : 'Available documents'}
          </p>
        </div>
        {isManagement && (
          <Button
            onClick={() => setUploadDialogOpen(true)}
            data-testid="upload-document-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload Document
          </Button>
        )}
      </div>

      {/* Management Only - Training Guide PDF */}
      {isManagement && (
        <Card className="bg-primary/5 border-primary/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              Official Training Guide
              <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full flex items-center gap-1">
                <Lock className="h-3 w-3" />
                Management Only
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Complete training guide for Renee, Biz, and Ozzy covering all app features including events, commissions, staff management, tasks, and financial tracking.
            </p>
            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => navigate('/training-guide')}
                data-testid="download-training-pdf"
                className="w-fit"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                View & Download Training Guide
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {Object.keys(grouped).length === 0 && !isManagement ? (
        <Card className="p-12 text-center bg-card border-border/50">
          <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <div className="text-muted-foreground">No documents available</div>
        </Card>
      ) : Object.keys(grouped).length === 0 ? (
        <Card className="p-12 text-center bg-card border-border/50">
          <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <div className="text-muted-foreground">No additional documents uploaded yet</div>
          <p className="text-xs text-muted-foreground mt-2">Use the Upload button to add documents</p>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, docs]) => (
            <div key={cat}>
              <h2 className="text-2xl font-semibold font-heading mb-4 text-primary capitalize">
                {cat.replace('_', ' ')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {docs.map((doc) => (
                  <Card key={doc.id} className="bg-card border-border/50 hover:border-primary/50 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <FileText className="h-8 w-8 text-primary" />
                        {isManagement && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(doc.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <h3 className="font-semibold mb-2 line-clamp-2">{doc.title}</h3>
                      <p className="text-xs text-muted-foreground mb-3">
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => window.open(doc.file_url, '_blank')}
                      >
                        View Document
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Dialog - Management Only */}
      {isManagement && (
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent className="bg-card">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading">Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Document Title *</Label>
                <Input
                  id="title"
                  placeholder="Training Manual 2025"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground focus:outline-none focus:ring-1 focus:ring-primary h-11"
                >
                  <option value="training">Training Materials</option>
                  <option value="policies">Policies & Procedures</option>
                  <option value="templates">Templates</option>
                  <option value="general">General</option>
                </select>
              </div>
              <div>
                <Label htmlFor="fileUrl">File URL *</Label>
                <Input
                  id="fileUrl"
                  placeholder="https://drive.google.com/file/..."
                  value={fileUrl}
                  onChange={(e) => setFileUrl(e.target.value)}
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Upload your file to Google Drive, Dropbox, or any file hosting service and paste the link here
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="managementOnly"
                  checked={managementOnly}
                  onChange={(e) => setManagementOnly(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="managementOnly" className="text-sm font-normal cursor-pointer">
                  Management only (hide from employees)
                </Label>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpload} className="bg-primary text-primary-foreground">
                  Upload
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
